import React, { ChangeEvent, useContext, useState } from "react";
import { Alert, Box, IconButton, Stack, TextField } from "@mui/material";

import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from "react-i18next";

import { InfegyUser, UserPreference, updateCurrentUser } from "system/InfegyUser";
import Avatar from "components/uiElements/avatar/Avatar";
import APIRequest from "system/APIRequest";
import { useSnackbar } from "notistack";

interface UserBannerProps {
  /**
   * The user to show the banner for.
   */
 user: InfegyUser
}

export default function UserBanner({ user }: UserBannerProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [isEditingName, setIsEditingName] = useState(false);
  const [avatar, setAvatar] = useState(user?.getPreference(UserPreference.AVATAR));
  const [name, setName] = useState("");
  const [userName, setUserName] = useState(user.name);
  const [errorMessage, setErrorMessage] = useState("");

  function handleEditName(): void {
    setName("");
    setErrorMessage("");
    setIsEditingName(true);
  }

  function handleNameChange(event: ChangeEvent<HTMLInputElement>): void {
    setName(event.target.value);
  }

  async function handleSaveName() {
    const body = {
      name: name
    };
    try {
      await APIRequest(`obj/user/${user.id}`, {}, {
        method: "PUT",
        body: JSON.stringify(body)
      });
      APIRequest(`obj/user/${user.id}`).then((response) => {
        // The `InfegyUser.setCurrentUser` function won't work here because it expects a 
        // different response output that is returned from the "me" endpoint.
        updateCurrentUser(user, {name: response.output.name});
        // Since the `user` prop being passed in is a global variable from `InfegyUser`, it will 
        // not trigger a React update when it changes.  By using a local state variable we can 
        // create a hook that will cause the component to update to show the new value.
        setUserName(response.output.name);
      });
    } catch (error: any) {
      setErrorMessage(error.server_message);
      return;
    }
    enqueueSnackbar(i18n.t("profile-page.name-saved"), { variant: "success" });
    setIsEditingName(false);
  }

  function handleCancelEditName(): void {
    setIsEditingName(false);
  }
  
  return (
    <Box sx={{display: "flex", flexDirection: "row", padding: 0}}>
      <Avatar isEditable={true} autoSave={true} user={user} size={64} />
      <Stack spacing={0} sx={{
        marginLeft: 2
      }}>
        {isEditingName &&
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center"
            }}>
            <TextField autoComplete="off" fullWidth={true} placeholder={user.name} onChange={handleNameChange} size="small" value={name} variant="outlined" />
            <IconButton onClick={handleSaveName} color="success">
              <CheckIcon />
            </IconButton>
            <IconButton onClick={handleCancelEditName} color="warning">
              <BlockIcon />
            </IconButton>
          </Box>
        }
        {!isEditingName &&
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              fontWeight: "bold",
              fontSize: "1.5em"
            }}>
            <Box>{userName}</Box>
            <IconButton onClick={handleEditName}>
              <EditIcon />
            </IconButton>
          </Box>
        }
        {errorMessage && isEditingName && <Alert variant="filled" severity="error">{errorMessage}</Alert>}
        <Box>
          {user.email}
        </Box>
      </Stack>
    </Box>
  );
}
